import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { submitLead } from "@/lib/leadSubmit";
import { getTrackingData } from "@/hooks/useTrackingData";
import { trackLeadSubmission } from "@/lib/metaLeadTracking";

interface CounterProps {
  target: number;
  suffix: string;
  label: string;
  inView: boolean;
  delay: number;
}

const AnimatedCounter = ({ target, suffix, label, inView, delay }: CounterProps) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        current += increment;
        if (current >= target) {
          setCount(target);
          clearInterval(interval);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timer);
  }, [inView, target, delay]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: delay / 1000 }}
      className="text-center"
    >
      <div className="text-4xl font-bold text-primary md:text-5xl">
        {count.toLocaleString()}{suffix}
      </div>
      <p className="mt-1 text-sm font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
    </motion.div>
  );
};

const states = [
  { value: "QLD", label: "Queensland" },
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "SA", label: "South Australia" },
];

import { PRODUCT_CATEGORY_LABELS } from "@/data/categoryOptions";
const categories = PRODUCT_CATEGORY_LABELS;

export function DeliveryCommitmentQuote() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    state: "",
    category: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.state) newErrors.state = "Please select a state";
    if (!formData.category) newErrors.category = "Please select a category";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const nameParts = formData.name.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "-";

      await submitLead({
        type: "quote",
        firstName,
        lastName,
        email: formData.email,
        phone: formData.phone,
        state: formData.state,
        category: formData.category,
        subject: `Homepage Quote – ${formData.category}`,
        message: formData.message,
        ...trackLeadSubmission({ contentName: "Homepage Quote", contentCategory: formData.category }),
      });

      setIsSubmitted(true);
      toast({
        title: "Quote Request Submitted!",
        description: "We'll get back to you within 24-48 hours.",
      });

      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: "", email: "", phone: "", state: "", category: "", message: "" });
      }, 3000);
    } catch (error) {
      console.error("Quote submission error:", error);
      toast({
        title: "Submission Failed",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <section ref={ref} className="section-padding bg-muted/30">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-background p-8 shadow-lg md:p-12"
        >
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-0">
            {/* LEFT COLUMN - Delivery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:border-r lg:border-border/50 lg:pr-10"
            >
              <h3 className="mb-4 text-2xl font-bold text-foreground md:text-3xl">
                Where do we deliver to?
              </h3>
              <p className="mb-6 text-muted-foreground">
                We deliver across QLD, NSW, VIC & SA. Delivery availability depends on product and location.
              </p>
              
              {/* State Pills */}
              <div className="mb-8 flex flex-wrap gap-2">
                {states.map((state, index) => (
                  <motion.span
                    key={state.value}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {state.label}
                  </motion.span>
                ))}
              </div>
              
              {/* Animated Counters */}
              <div className="grid grid-cols-2 gap-6">
                <AnimatedCounter target={1500} suffix="+" label="Successful Projects" inView={inView} delay={300} />
                <AnimatedCounter target={5000} suffix="+" label="Happy Customers" inView={inView} delay={500} />
              </div>
            </motion.div>

            {/* RIGHT COLUMN - Quote Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:pl-10"
            >
              <h3 className="mb-6 text-2xl font-bold text-foreground md:text-3xl">
                Request A Quote
              </h3>
              
              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <CheckCircle className="h-8 w-8 text-primary" />
                  </div>
                  <h4 className="mb-2 text-xl font-bold text-foreground">Quote Submitted!</h4>
                  <p className="text-muted-foreground">We'll get back to you within 24-48 hours.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Input placeholder="Name" value={formData.name} onChange={(e) => handleInputChange("name", e.target.value)} className={errors.name ? "border-destructive" : ""} />
                      {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                    </div>
                    <div className="space-y-2">
                      <Input type="email" placeholder="Email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} className={errors.email ? "border-destructive" : ""} />
                      {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Input type="tel" placeholder="Phone Number" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} className={errors.phone ? "border-destructive" : ""} />
                    {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
                  </div>
                  
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Select value={formData.state} onValueChange={(value) => handleInputChange("state", value)}>
                        <SelectTrigger className={errors.state ? "border-destructive" : ""}><SelectValue placeholder="Choose State" /></SelectTrigger>
                        <SelectContent>{states.map((state) => (<SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>))}</SelectContent>
                      </Select>
                      {errors.state && <p className="text-xs text-destructive">{errors.state}</p>}
                    </div>
                    <div className="space-y-2">
                      <Select value={formData.category} onValueChange={(value) => handleInputChange("category", value)}>
                        <SelectTrigger className={errors.category ? "border-destructive" : ""}><SelectValue placeholder="Product Category" /></SelectTrigger>
                        <SelectContent>{categories.map((cat) => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}</SelectContent>
                      </Select>
                      {errors.category && <p className="text-xs text-destructive">{errors.category}</p>}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Textarea placeholder="Message" rows={4} value={formData.message} onChange={(e) => handleInputChange("message", e.target.value)} className={errors.message ? "border-destructive" : ""} />
                    {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
                  </div>
                  
                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : (<>Submit Quote <Send className="ml-2 h-4 w-4" /></>)}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
